import { NormalBreadcrumb } from '@/components/Breadcrumb';
import { useRequest } from 'ahooks';
import { Badge, Button, Card, Col, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import { Column, Line } from '@ant-design/plots';
import { getServices } from '@/api/cloud';
import { getDeviceMessage, getDeviceOverview } from '@/api/drive';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import 'swiper/css';
import './swiper.less';
import 'swiper/css/pagination';

function FirstCard({ deviceId, setDeviceId, setDeviceContainerName }: { deviceId: string; setDeviceId: (id: string) => void; setDeviceContainerName: (name: string) => void }) {
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const [isBegining, setIsBegining] = useState(true);
  const [isEnding, setIsEnding] = useState(false);

  const goToNextSlide = () => {
    if (swiperInstance) {
      swiperInstance.slideNext();
    }
  };
  const goToPrevSlide = () => {
    if (swiperInstance) {
      swiperInstance.slidePrev();
    }
  };

  const { data } = useRequest(getDeviceOverview);
  if (!deviceId) {
    const id = data?.result?.[0]?.id;
    setDeviceId(id || '');
    setDeviceContainerName(data?.result?.[0]?.containerName || '');
  }

  return (
    <Card
      className="m20 swiper-wrapper-card"
      title="数据概览"
      extra={
        <div className="flex justify-center mt5" style={{ gap: '12px' }}>
          <Button
            size="small"
            icon={<LeftOutlined />}
            onClick={goToPrevSlide}
            disabled={isBegining}
            style={{
              border: 'none',
              background: isBegining ? '#f5f5f5' : '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          />
          <Button
            size="small"
            icon={<RightOutlined />}
            onClick={goToNextSlide}
            disabled={isEnding}
            style={{
              border: 'none',
              background: isEnding ? '#f5f5f5' : '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          />
        </div>
      }
    >
      <Swiper
        spaceBetween={20}
        slidesPerView={4.5}
        pagination={{
          clickable: true,
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active',
        }}
        onSwiper={(swiper: any) => setSwiperInstance(swiper)}
        onActiveIndexChange={(e: any) => {
          setIsBegining(e.isBeginning);
          setIsEnding(e.isEnd);
        }}
        modules={[Navigation, Pagination]}
        className="mySwiper"
        breakpoints={{
          768: {
            slidesPerView: 2.5,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3.5,
            spaceBetween: 20,
          },
          1440: {
            slidesPerView: 4.5,
            spaceBetween: 20,
          },
        }}
      >
        {data?.result.map((item) => (
          <SwiperSlide key={item.id}>
            <Badge.Ribbon text={item.runStatus === 1 ? '在线' : '离线'} color={item.runStatus === 1 ? 'green' : 'gray'}>
              <Card
                style={{
                  height: '100%',
                  cursor: 'pointer',
                  backgroundColor: deviceId === item.id ? '#f5f5f5' : 'white',
                  borderRadius: '4px',
                }}
                onClick={() => {
                  setDeviceId(item.id);
                  setDeviceContainerName(item.containerName);
                }}
              >
                <div className="flex-vertical justify-space-between" style={{ height: '100%', gap: '8px' }}>
                  <div className="flex justify-space-between">
                    <div style={{ color: '#666', fontSize: '14px' }}>{item.name}</div>

                    {/* <Badge status="success" /> */}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold' }}>设备数: {item.deviceCount}个</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ color: '#52c41a' }}>在线: {item.deviceOnlineCount}个</div>
                    <div style={{ color: '#999' }}>离线: {item.deviceOfflineCount}个</div>
                  </div>
                </div>
              </Card>
            </Badge.Ribbon>
          </SwiperSlide>
        ))}
      </Swiper>
    </Card>
  );
}

function SingleLine({ title, tag, per, deviceContainerName }: { title: string; tag?: string; per: string; deviceContainerName: string }) {
  const [metric_tags] = useState<string>(tag || 'net_work_tx');

  const [metric_time] = useState<string>('2min');
  const [list, setList] = useState<{ name: string; time: string; value: number }[]>([]);
  const { run } = useRequest(getServices, {
    defaultParams: [{ metric_time, metric_tags, container_name: deviceContainerName }],
    manual: true, // 设置为手动触发
    ready: !!deviceContainerName, // 只有当 deviceId 存在时才开始轮询
    onSuccess(data) {
      const tList = data.result.series.reduce<{ name: string; time: string; value: number }[]>((pre, cur) => {
        cur.times.forEach((time, index) => {
          pre.push({
            name: cur.container_name,
            time,
            value: cur.values[index],
          });
        });
        return pre;
      }, []);
      setList(tList);
    },
  });

  useEffect(() => {
    if (deviceContainerName) {
      run({ metric_time, metric_tags, container_name: deviceContainerName });
    }
  }, [deviceContainerName, metric_time, run]);
  return (
    <Card className="" title={<>{title}</>}>
      <div className="" style={{ width: '100%', height: '300px', paddingTop: '20px' }}>
        <Line
          data={list}
          xField="time"
          yField="value"
          seriesField="name"
          legend={false}
          yAxis={{ title: { text: `${title}(${per})` }, label: {} }}
          // lineStyle={}
          lineStyle={{ lineWidth: 1.5 }}
          autoFit
          smooth
          tooltip={{ formatter: (data) => ({ name: title, value: `${data.value}${per}` }) }}
        ></Line>
      </div>
    </Card>
  );
}

function UpdownColumn({ title, deviceId }: { title: string; deviceId: string }) {
  const [upList, setUpList] = useState<{ time: string; value: number }[]>([]);
  const [downList, setDownList] = useState<{ time: string; value: number }[]>([]);
  const { run } = useRequest(getDeviceMessage, {
    manual: true, // 设置为手动触发
    pollingInterval: 1000, // 每秒轮询一次
    ready: !!deviceId, // 只有当 deviceId 存在时才开始轮询
    onSuccess(data) {
      const _upList: any = [];
      const _downList: any = [];
      data.result.times.forEach((time: string, index: number) => {
        _upList.push({
          time,
          value: data.result.up_value[index],
        });

        _downList.push({
          time,
          value: data.result.dp_value[index],
        });
      });

      setUpList(_upList);
      setDownList(_downList);
    },
  });

  useEffect(() => {
    if (deviceId) {
      run({ id: deviceId });
    }
  }, [deviceId, run]);

  return (
    <Card className="" title={title} style={{ paddingBottom: '20px' }}>
      <div>
        消息上行 {upList[upList.length - 1]?.value} 条/秒
        <div className="" style={{ width: '100%', height: '60px', paddingTop: '20px', paddingBottom: '20px' }}>
          <Column
            data={upList}
            xField="time"
            yField="value"
            xAxis={false}
            yAxis={false}
            color="#1677ff"
            animation={false}
            tooltip={{ formatter: (data) => ({ name: '消息上行', value: `${data.value}  条/秒` }) }}
            columnBackground={{
              style: {
                fill: '#1677ff',
                fillOpacity: 0.1,
              },
            }}
          />
        </div>
      </div>
      <div style={{ marginTop: '40px' }}>
        消息下行 {downList[downList.length - 1]?.value} 条/秒
        <div className="" style={{ width: '100%', height: '60px', paddingTop: '20px', paddingBottom: '20px' }}>
          <Column
            data={downList}
            xField="time"
            yField="value"
            xAxis={false}
            yAxis={false}
            color="#722ed1"
            animation={false}
            tooltip={{ formatter: (data) => ({ name: '消息下行', value: `${data.value}  条/秒` }) }}
            columnBackground={{
              style: {
                fill: '#722ed1',
                fillOpacity: 0.1,
              },
            }}
          />
        </div>
      </div>
    </Card>
  );
}

export default function Drive() {
  const [deviceId, setDeviceId] = useState('');
  const [deviceContainerName, setDeviceContainerName] = useState('');

  return (
    <>
      <NormalBreadcrumb></NormalBreadcrumb>
      <FirstCard deviceId={deviceId} setDeviceId={setDeviceId} setDeviceContainerName={setDeviceContainerName}></FirstCard>
      <div className="m20">
        <Row gutter={[20, 20]}>
          <Col span={24}>
            <UpdownColumn title="消息趋势" deviceId={deviceId}></UpdownColumn>
          </Col>
          <Col span={12}>
            <SingleLine title="CPU使用率" tag="cpu_percent" per="%" deviceContainerName={deviceContainerName}></SingleLine>
          </Col>
          <Col span={12}>
            <SingleLine title="内存占用" tag="memory_usage" per="MB" deviceContainerName={deviceContainerName}></SingleLine>
          </Col>
        </Row>
      </div>
    </>
  );
}
